import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import CreateEventForm from "./CreateEventForm";
import EventListTable from "./events-list/EventListTable";
import { Event } from "@/types/event";
import { useAdminEvents, EVENTS_PER_PAGE, EVENTS_QUERY_KEY } from "@/hooks/useAdminEvents";
import { EventStatsCard } from "./events/EventStatsCard";
import { EventColumnFilters } from "./events/EventColumnFilters";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

const EventsList = () => {
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const queryClient = useQueryClient();

  const {
    events,
    isLoading,
    isEmpty,
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,
    currentStatus,
    currentPage,
    setCurrentPage,
    totalPages,
    totalEvents,
    sortField,
    sortDirection,
    handleSort,
    toggleArchiveEvent,
    toggleCancelEvent,
    deleteTag,
  } = useAdminEvents();

  if (editingEvent) {
    return (
      <CreateEventForm
        event={editingEvent}
        onClose={() => {
          setEditingEvent(null);
          queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] });
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview Card */}
      <EventStatsCard />

      {/* Events Table Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-secondary" />
                Events Management
              </CardTitle>
              <CardDescription>
                View and manage events
              </CardDescription>
            </div>
            <EventColumnFilters
              filterTags={filterTags}
              addFilter={addFilter}
              removeFilter={removeFilter}
              clearAllFilters={clearAllFilters}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">

          {isLoading ? (
            <div className="text-muted-foreground py-8 text-center">Loading events...</div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <EventListTable
                events={events}
                isEmpty={isEmpty}
                onEditEvent={setEditingEvent}
                onDeleteTag={deleteTag}
                onToggleArchiveEvent={toggleArchiveEvent}
                onToggleCancelEvent={toggleCancelEvent}
                currentStatus={currentStatus}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </div>
          )}

          {totalEvents > 0 && (
            <div className="flex items-center justify-between mt-3">
              <p className="text-sm text-muted-foreground">
                {totalPages > 1 
                  ? `Showing ${(currentPage - 1) * EVENTS_PER_PAGE + 1} to ${Math.min(currentPage * EVENTS_PER_PAGE, totalEvents)} of ${totalEvents} events`
                  : `Showing ${totalEvents} events of ${totalEvents}`
                }
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EventsList;
